/**
 * This module handles numeric/continuous data visualization where values are mapped to a colour gradient.
 * It's used when you have a range of numbers (e.g., depth: 10.2m → 15.8m) and want to represent them
 * visually with a smooth colour transition.
 */

import { BASE_COLOURS } from '~config/colours';
import type { BaseColour } from '~types';
import { createNumericScale } from './numeric-scale';
import type { ColourScale } from './types';

/** Default number of legend steps for continuous scales */
const DEFAULT_LEGEND_STEPS = 6;

/** Gradient configuration for OKLCH interpolation */
const GRADIENT_CONFIG = {
  LIGHTNESS: { MIN: 0.35, MAX: 0.85, OFFSET: 0.25 },
  CHROMA: { MIN: 0.05, MAX: 0.3, OFFSET: 0.1 },
} as const;

/** Options for creating a continuous colour scale */
type ContinuousScaleOptions = {
  baseColour?: BaseColour;
  steps?: number;
  formatLabel?: (value: number) => string;
};

/**
 * Interpolates a numeric value to an OKLCH colour string based on the provided range and base colour.
 *
 * @param {number} value - The numeric value to convert to a colour.
 * @param {number} min - The minimum value of the range.
 * @param {number} max - The maximum value of the range.
 * @param {BaseColour} [baseColour=BASE_COLOURS.BLUE] - The base colour to use for interpolation (default is blue).
 * @return {string} The resulting OKLCH colour string (e.g., 'oklch(0.6 0.15 220)').
 *
 * @example
 * ```ts
 * const colour = interpolateOklch(15, 10, 20); // Interpolates value 15 in range [10, 20] to a colour
 * console.log(colour); // e.g., 'oklch(0.6 0.15 220)'
 * ```
 */
function interpolateOklch(value: number, min: number, max: number, baseColour: BaseColour = BASE_COLOURS.BLUE): string {
  if (max === min) {
    return `oklch(${baseColour.l.toFixed(2)} ${baseColour.c.toFixed(2)} ${baseColour.h.toFixed(0)})`;
  }

  const t = Math.max(0, Math.min(1, (value - min) / (max - min)));
  const { LIGHTNESS, CHROMA } = GRADIENT_CONFIG;

  const startL = Math.min(LIGHTNESS.MAX, baseColour.l + LIGHTNESS.OFFSET);
  const endL = Math.max(LIGHTNESS.MIN, baseColour.l - LIGHTNESS.OFFSET);
  const startC = Math.max(CHROMA.MIN, baseColour.c - CHROMA.OFFSET);
  const endC = Math.min(CHROMA.MAX, baseColour.c + CHROMA.OFFSET);

  const l = startL + (endL - startL) * t;
  const c = startC + (endC - startC) * t;

  return `oklch(${l.toFixed(2)} ${c.toFixed(2)} ${baseColour.h.toFixed(0)})`;
}

/**
 * Create a continuous colour scale configuration based on the provided data and property. It calculates the range of
 * values for the specified property and generates a colour gradient accordingly. The resulting scale includes a legend.
 *
 * @template T - The type of data items (must be an object with the specified property as a number).
 * @param {T[]} data - The array of data items to analyze for the colour scale.
 * @param {(keyof T & string)} property - The property of the data items to base the colour scale on (must be numeric).
 * @param {ContinuousScaleOptions} [options={}] - Optional configuration for the colour scale (base colour, number of legend steps, label formatting).
 * @return {Extract<ColourScale, { type: 'continuous' }>} A continuous colour scale configuration.
 *
 * @example
 * ```ts
 * const scale = createContinuousScale(blocks, 'depth', {
 *   baseColour: BASE_COLOURS.GREEN,
 *   steps: 5,
 *   formatLabel: (v) => `${v} m`,
 * });
 *
 * // Get colour for a value
 * const colour = scale.getColour(15.5);
 *
 * // Access legend for UI
 * scale.legend.forEach(item => console.log(item.label, item.colour));
 * ```
 */
function createContinuousScale<T extends Record<string, unknown>>(
  data: T[],
  property: keyof T & string,
  options: ContinuousScaleOptions = {},
): Extract<ColourScale, { type: 'continuous' }> {
  const {
    baseColour = BASE_COLOURS.BLUE,
    steps = DEFAULT_LEGEND_STEPS,
    formatLabel = (v: number) => v.toFixed(2),
  } = options;

  return createNumericScale(data, property, {
    steps,
    formatLabel,
    interpolate: (value, min, max) => interpolateOklch(value, min, max, baseColour),
  });
}

export { type ContinuousScaleOptions, createContinuousScale, DEFAULT_LEGEND_STEPS, interpolateOklch };
